import React, { forwardRef } from 'react';
import type { MotionProps } from 'framer-motion';
import { cls, cn } from '@/lib/utils';
import MotionImage, { type MotionImageProperties } from '../Motion/image.tsx';
import ResponsiveWrapper, {
  type ResponsiveProperties,
} from './responsive-wrapper.tsx';
import { ZStack, ZStackChild } from './z-stack.tsx';

interface BackgroundImageProperties extends ResponsiveProperties, MotionProps {
  src: string;
  alt: string;
  overlay?: string;
  foreground?: string;
  className?: string;
  motionProps?: MotionProps;
  imageProps?: Omit<MotionImageProperties, 'src' | 'alt'>;
  width?: number;
  height?: number;
  fill: boolean;
  sizes: string;
}

const BackgroundImage = forwardRef<HTMLDivElement, BackgroundImageProperties>(
  (
    {
      src,
      alt,
      width,
      height,
      fill = true,
      sizes = '100vw',
      overlay = '',
      sm,
      md,
      lg,
      motionProps = {},
      imageProps = {},
      className = '',
      foreground = '',
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <ResponsiveWrapper sm={sm} md={md} lg={lg}>
        <ZStack
          id="background-img"
          ref={ref}
          className={cn('relative w-full h-full', className)}
          {...motionProps}
          {...props}
        >
          {/* Background Image */}
          <ZStackChild zIndex={0}>
            <MotionImage
              src={src}
              alt={alt}
              width={width}
              height={height}
              fill={fill}
              sizes={sizes}
              showSkeleton={true}
              wrapperClassName={cls`absolute inset-0 w-full h-full`}
              skeletonClassName={cls`absolute inset-0 w-full h-full`}
              style={{ objectFit: 'cover', ...imageProps.style }}
              {...imageProps}
            />
          </ZStackChild>

          {/* Optional Overlay */}
          {overlay && (
            <ZStackChild
              id="img-overlay"
              zIndex={5}
              className={cn('absolute inset-0', overlay)}
            />
          )}

          {/* Foreground Content */}
          <ZStackChild
            id="img-foreground-content"
            zIndex={10}
            className={cn('relative h-full w-full', foreground)}
          >
            {children}
          </ZStackChild>
        </ZStack>
      </ResponsiveWrapper>
    );
  },
);

BackgroundImage.displayName = 'BackgroundImage';

export default BackgroundImage;
